import { useCallback, useEffect, useState } from 'react';
import { api, type InstallerRow } from '../lib/api';
import { Empty, ErrorBanner, Panel, Spinner } from '../lib/ui';

/**
 * The installer roster, editable in place.
 *
 * It was read-only, which meant the one field that stops a dispatch — the email
 * address — could only be fixed by someone with database access. Five of the
 * nine installers shipped with `needs-address@harkey.invalid` precisely because
 * nobody had the real one at import time; there has to be a way to put it right
 * from the app.
 */

/** Blank form, also the shape of a new installer. */
const EMPTY: Editable = {
  companyName: '',
  primaryContactName: '',
  email: '',
  phone: '',
  capCrane: false,
  capBucket: false,
  capHighClimb: false,
  capDigital: false,
  insuranceExpiresOn: '',
  w9OnFile: false,
  isActive: true,
};

interface Editable {
  companyName: string;
  primaryContactName: string;
  email: string;
  phone: string;
  capCrane: boolean;
  capBucket: boolean;
  capHighClimb: boolean;
  capDigital: boolean;
  insuranceExpiresOn: string;
  w9OnFile: boolean;
  isActive: boolean;
}

function toEditable(row: InstallerRow): Editable {
  return {
    companyName: row.companyName,
    primaryContactName: row.primaryContactName ?? '',
    email: row.email,
    phone: row.phone ?? '',
    capCrane: row.capCrane,
    capBucket: row.capBucket,
    capHighClimb: row.capHighClimb,
    capDigital: row.capDigital,
    insuranceExpiresOn: row.insuranceExpiresOn ?? '',
    w9OnFile: row.w9OnFile,
    isActive: row.isActive,
  };
}

function toPayload(d: Editable): Record<string, unknown> {
  return {
    companyName: d.companyName.trim(),
    primaryContactName: d.primaryContactName.trim() === '' ? null : d.primaryContactName.trim(),
    email: d.email.trim(),
    phone: d.phone.trim() === '' ? null : d.phone.trim(),
    capCrane: d.capCrane,
    capBucket: d.capBucket,
    capHighClimb: d.capHighClimb,
    capDigital: d.capDigital,
    insuranceExpiresOn: d.insuranceExpiresOn === '' ? null : d.insuranceExpiresOn,
    w9OnFile: d.w9OnFile,
    isActive: d.isActive,
  };
}

/** The placeholder given to installers imported without a usable address. */
const PLACEHOLDER_DOMAIN = '@harkey.invalid';

export function InstallersPage() {
  const [rows, setRows] = useState<InstallerRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Editable>(EMPTY);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get<{ installers: InstallerRow[] }>('/api/admin/installers')
      .then((d) => {
        setRows(d.installers);
        setError(null);
      })
      .catch((e: unknown) => setError(e));
  }, []);

  useEffect(load, [load]);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (creating) await api.post('/api/admin/installers', toPayload(draft));
      else if (editingId !== null) {
        await api.patch(`/api/admin/installers/${editingId}`, toPayload(draft));
      }
      setEditingId(null);
      setCreating(false);
      setDraft(EMPTY);
      load();
    } catch (e: unknown) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const today = new Date().toISOString().slice(0, 10);
  const soon = new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);
  const placeholders = (rows ?? []).filter((r) => r.email.endsWith(PLACEHOLDER_DOMAIN)).length;

  return (
    <div className="space-y-4">
      {error !== null && <ErrorBanner error={error} />}

      {placeholders > 0 && (
        <p className="border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          {placeholders} installer{placeholders === 1 ? ' has' : 's have'} a placeholder address
          ending <code>{PLACEHOLDER_DOMAIN}</code>, which can never receive mail. They stay
          deactivated until a real address is set — that is what stops a dispatch vanishing.
        </p>
      )}

      {(creating || editingId !== null) && (
        <Panel title={creating ? 'New installer' : 'Edit installer'}>
          <InstallerForm draft={draft} setDraft={setDraft} />
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => void save()}
              disabled={busy || draft.companyName.trim() === '' || draft.email.trim() === ''}
              className="bg-harkey-gold px-3 py-1 text-xs font-semibold text-black disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => {
                setEditingId(null);
                setCreating(false);
                setDraft(EMPTY);
              }}
              className="border border-neutral-300 px-3 py-1 text-xs dark:border-neutral-700"
            >
              Cancel
            </button>
          </div>
        </Panel>
      )}

      <Panel
        title={`Installers (${rows?.length ?? 0})`}
        actions={
          !creating && editingId === null ? (
            <button
              onClick={() => {
                setCreating(true);
                setDraft(EMPTY);
              }}
              className="bg-harkey-gold px-2.5 py-1 text-xs font-semibold text-black"
            >
              + Add installer
            </button>
          ) : undefined
        }
      >
        {rows === null ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty>No installers yet.</Empty>
        ) : (
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-neutral-500 dark:border-neutral-800">
                <th className="py-1 pr-3 font-medium">Company</th>
                <th className="py-1 pr-3 font-medium">Contact</th>
                <th className="py-1 pr-3 font-medium">Capabilities</th>
                <th className="py-1 pr-3 font-medium">W-9</th>
                <th className="py-1 pr-3 font-medium">Insurance</th>
                <th className="py-1 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => {
                const caps = [
                  i.capCrane && 'crane',
                  i.capBucket && 'bucket',
                  i.capHighClimb && 'climb',
                  i.capDigital && 'digital',
                ].filter(Boolean);
                const expired = i.insuranceExpiresOn !== null && i.insuranceExpiresOn < today;
                const expiring =
                  !expired && i.insuranceExpiresOn !== null && i.insuranceExpiresOn < soon;
                const placeholder = i.email.endsWith(PLACEHOLDER_DOMAIN);

                return (
                  <tr
                    key={i.id}
                    className={`border-b border-neutral-100 dark:border-neutral-900 ${
                      i.isActive ? '' : 'opacity-60'
                    }`}
                  >
                    <td className="py-1.5 pr-3">
                      <span className="font-medium">{i.companyName}</span>
                      {!i.isActive && <span className="ml-1 text-neutral-500">(inactive)</span>}
                    </td>
                    <td className="py-1.5 pr-3">
                      {i.primaryContactName !== null && (
                        <span className="block">{i.primaryContactName}</span>
                      )}
                      <span className={placeholder ? 'text-red-600 dark:text-red-400' : ''}>
                        {i.email}
                      </span>
                      {i.phone !== null && <span className="block text-neutral-500">{i.phone}</span>}
                    </td>
                    <td className="py-1.5 pr-3">{caps.length > 0 ? caps.join(', ') : '—'}</td>
                    <td className="py-1.5 pr-3">{i.w9OnFile ? 'yes' : 'no'}</td>
                    <td
                      className={`py-1.5 pr-3 tabular-nums ${
                        expired
                          ? 'font-semibold text-red-600 dark:text-red-400'
                          : expiring
                            ? 'text-amber-700 dark:text-amber-400'
                            : ''
                      }`}
                    >
                      {i.insuranceExpiresOn ?? '—'}
                      {expired && ' — expired'}
                    </td>
                    <td className="py-1.5">
                      <button
                        onClick={() => {
                          setCreating(false);
                          setEditingId(i.id);
                          setDraft(toEditable(i));
                        }}
                        className="border border-neutral-300 px-1.5 py-0.5 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

function InstallerForm({
  draft,
  setDraft,
}: {
  draft: Editable;
  setDraft: (d: Editable) => void;
}) {
  const set = <K extends keyof Editable>(key: K, value: Editable[K]): void =>
    setDraft({ ...draft, [key]: value });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <Text label="Company" value={draft.companyName} onChange={(v) => set('companyName', v)} width="w-64" />
        <Text
          label="Contact name"
          value={draft.primaryContactName}
          onChange={(v) => set('primaryContactName', v)}
          width="w-48"
        />
        <Text
          label="Email"
          value={draft.email}
          onChange={(v) => set('email', v)}
          width="w-64"
          type="email"
        />
        <Text label="Phone" value={draft.phone} onChange={(v) => set('phone', v)} width="w-40" />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Text
          label="Insurance expires"
          value={draft.insuranceExpiresOn}
          onChange={(v) => set('insuranceExpiresOn', v)}
          width="w-40"
          type="date"
        />
        <Check label="W-9 on file" checked={draft.w9OnFile} onChange={(v) => set('w9OnFile', v)} />
        <Check label="Active" checked={draft.isActive} onChange={(v) => set('isActive', v)} />
      </div>

      <fieldset className="flex flex-wrap items-center gap-3">
        <legend className="text-xs text-neutral-600 dark:text-neutral-400">Capabilities</legend>
        <Check label="Crane" checked={draft.capCrane} onChange={(v) => set('capCrane', v)} />
        <Check label="Bucket" checked={draft.capBucket} onChange={(v) => set('capBucket', v)} />
        <Check label="High climb" checked={draft.capHighClimb} onChange={(v) => set('capHighClimb', v)} />
        <Check label="Digital" checked={draft.capDigital} onChange={(v) => set('capDigital', v)} />
      </fieldset>
    </div>
  );
}

function Text({
  label,
  value,
  onChange,
  width,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  width: string;
  type?: string;
}) {
  return (
    <label className="text-xs">
      <span className="mb-1 block text-neutral-600 dark:text-neutral-400">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${width} border border-neutral-300 px-1.5 py-1 dark:border-neutral-700 dark:bg-neutral-950`}
      />
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
